import React, { useState } from "react";
import { X, MapPin } from "lucide-react";
import {
  ETHNICITIES,
  LANGUAGES,
  GENDER,
  GENDER_DISPLAY,
  ETHNICITY_DISPLAY,
} from "../../DropdownOptions/LanguageEthnicityGender.js";
import { getTodayInZone } from "../../APIs/providerApi.js";

// Returns tailwind classes for a select box based on whether it has an active value
const activeSelectClass = (hasValue) =>
  hasValue
    ? "w-full h-10 rounded border px-3 bg-blue-600 text-white border-blue-600 cursor-pointer"
    : "w-full h-10 rounded border px-3 bg-white text-gray-800 border-gray-300 cursor-pointer";

export default function AdvancedFiltersModal({
  open,
  onClose,
  stagedFilters,
  setStagedFilters,
  onApply,
  onReset,
  specialties,
  insurances,
  stateOptions,
  cityOptions,
  selectedStateId,
  setSelectedStateId,
  selectedCityId,
  setSelectedCityId,
  loadingStates,
  loadingCities,
  minYearsOfExperience,
  setMinYearsOfExperience,
  maxYearsOfExperience,
  setMaxYearsOfExperience,
  isAvailBetweenOpen,
  setIsAvailBetweenOpen,
}) {
  const [zipInput, setZipInput] = useState("");
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState("");

  const set = (key, value) =>
    setStagedFilters((prev) => ({
      ...prev,
      [key]: value === "" ? null : value,
    }));

  const handleZipCommit = () => {
    const zip = zipInput.trim();
    setStagedFilters((prev) => ({ ...prev, zipCode: zip || null }));
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setGeoError("Geolocation not supported by your browser.");
      return;
    }
    setGeoLoading(true);
    setGeoError("");
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setStagedFilters((prev) => ({
          ...prev,
          centerLatitude: coords.latitude,
          centerLongitude: coords.longitude,
          radiusInMiles: prev.radiusInMiles || 25,
          locationLabel: "Current Location",
        }));
        setGeoLoading(false);
      },
      () => {
        setGeoError("Location access denied.");
        setGeoLoading(false);
      },
    );
  };

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-40"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Slide-in panel */}
      <div
        className={`fixed inset-y-0 right-0 z-50 w-[420px] bg-white shadow-2xl flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${open ? "translate-x-0" : "translate-x-full"}`}
        aria-modal="true"
        role="dialog"
        aria-label="Advanced Filters"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Advanced Filters</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 text-gray-500"
            aria-label="Close filters"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable filter area */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Accepting New Patients */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Accepting New Patients
            </label>
            <select
              value={
                stagedFilters.acceptingNewPatients === null ||
                stagedFilters.acceptingNewPatients === undefined
                  ? ""
                  : String(stagedFilters.acceptingNewPatients)
              }
              onChange={(e) => {
                const v = e.target.value;
                setStagedFilters((prev) => ({
                  ...prev,
                  acceptingNewPatients: v === "" ? null : v === "true",
                }));
              }}
              className={activeSelectClass(
                stagedFilters.acceptingNewPatients !== null &&
                  stagedFilters.acceptingNewPatients !== undefined,
              )}
            >
              <option value="">Any</option>
              <option value="true">Accepting New Patients</option>
              <option value="false">Not Accepting</option>
            </select>
          </div>

          {/* Specialty */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Specialty
            </label>
            <select
              value={stagedFilters.specialtyName || ""}
              onChange={(e) => set("specialtyName", e.target.value)}
              className={activeSelectClass(!!stagedFilters.specialtyName)}
            >
              <option value="">All Specialties</option>
              {specialties.map((s) => (
                <option key={s.id} value={s.name}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          {/* Insurance — disabled until data accuracy is confirmed */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Insurance Accepted{" "}
              <span className="text-gray-400 font-normal normal-case">
                (Coming Soon)
              </span>
            </label>
            <select
              value=""
              disabled
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-400 bg-gray-50 cursor-not-allowed"
            >
              <option value="">Insurance Accepted</option>
            </select>
          </div>

          {/* Language */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Languages Spoken
            </label>
            <select
              value={stagedFilters.languageName || ""}
              onChange={(e) => set("languageName", e.target.value)}
              className={activeSelectClass(!!stagedFilters.languageName)}
            >
              <option value="">Languages Spoken</option>
              {LANGUAGES.map((l) => (
                <option key={l.id} value={l.name}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>

          {/* Ethnicity */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Ethnicity
            </label>
            <select
              value={stagedFilters.ethnicity || ""}
              onChange={(e) => set("ethnicity", e.target.value)}
              className={activeSelectClass(!!stagedFilters.ethnicity)}
            >
              <option value="">Ethnicity</option>
              {ETHNICITIES.map((eth) => (
                <option key={eth} value={eth}>
                  {ETHNICITY_DISPLAY[eth]}
                </option>
              ))}
            </select>
          </div>

          {/* Gender */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Gender
            </label>
            <select
              value={stagedFilters.gender || ""}
              onChange={(e) => set("gender", e.target.value)}
              className={activeSelectClass(!!stagedFilters.gender)}
            >
              <option value="">Gender</option>
              {GENDER.map((g) => (
                <option key={g} value={g}>
                  {GENDER_DISPLAY[g]}
                </option>
              ))}
            </select>
          </div>

          {/* Zip Code */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Zip Code
            </label>
            {stagedFilters.zipCode ? (
              <div className="flex items-center justify-between px-3 py-2 rounded border border-teal-400 bg-teal-50 text-teal-700 text-sm">
                <span className="flex items-center gap-1">
                  <MapPin size={13} />
                  {stagedFilters.zipCode}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setStagedFilters((prev) => ({ ...prev, zipCode: null }));
                    setZipInput("");
                  }}
                  className="text-teal-600 hover:text-teal-800"
                  aria-label="Clear zip"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <input
                type="text"
                placeholder="Enter Zip Code"
                value={zipInput}
                onChange={(e) => setZipInput(e.target.value)}
                onBlur={handleZipCommit}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleZipCommit();
                }}
                className="w-full h-10 rounded border px-3 text-sm border-gray-300 focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            )}
          </div>

          {/* My Location */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Nearby Search
            </label>
            {stagedFilters.centerLatitude && stagedFilters.centerLongitude ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between px-3 py-2 rounded border border-teal-400 bg-teal-50 text-teal-700 text-sm">
                  <span className="flex items-center gap-1">
                    <MapPin size={13} />
                    {stagedFilters.locationLabel || "Current Location"}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setStagedFilters((prev) => ({
                        ...prev,
                        centerLatitude: null,
                        centerLongitude: null,
                        radiusInMiles: 25,
                        locationLabel: "",
                      }))
                    }
                    className="text-teal-600 hover:text-teal-800"
                    aria-label="Clear location"
                  >
                    <X size={14} />
                  </button>
                </div>
                <select
                  value={stagedFilters.radiusInMiles || 25}
                  onChange={(e) =>
                    setStagedFilters((prev) => ({
                      ...prev,
                      radiusInMiles: Number(e.target.value),
                    }))
                  }
                  className={activeSelectClass(true)}
                >
                  <option value={25}>Within 25 miles</option>
                  <option value={10}>Within 10 miles</option>
                  <option value={5}>Within 5 miles</option>
                </select>
              </div>
            ) : (
              <div className="space-y-1">
                <button
                  type="button"
                  onClick={handleUseMyLocation}
                  disabled={geoLoading}
                  className="w-full h-10 rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-600 text-sm flex items-center justify-center gap-1 disabled:opacity-50"
                >
                  <MapPin size={13} />
                  {geoLoading ? "Locating…" : "Use My Location"}
                </button>
                {geoError && <p className="text-xs text-red-500">{geoError}</p>}
              </div>
            )}
          </div>

          {/* State */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              State
            </label>
            <select
              value={selectedStateId}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedStateId(val);
                const opt = stateOptions.find(
                  (o) => String(o.value) === String(val),
                );
                setStagedFilters((prev) => ({
                  ...prev,
                  state: opt?.code || null,
                  city: null,
                }));
                setSelectedCityId("");
              }}
              className={activeSelectClass(!!selectedStateId)}
            >
              <option value="">
                {loadingStates ? "Loading states…" : "State"}
              </option>
              {stateOptions.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          {/* City */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              City
            </label>
            <select
              value={selectedCityId}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedCityId(val);
                const opt = cityOptions.find(
                  (c) => String(c.value) === String(val),
                );
                setStagedFilters((prev) => ({
                  ...prev,
                  city: opt?.label || null,
                }));
              }}
              className={activeSelectClass(!!selectedCityId)}
            >
              <option value="">
                {selectedStateId
                  ? loadingCities
                    ? "Loading cities…"
                    : "City"
                  : "Select state first"}
              </option>
              {cityOptions.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          {/* Available Today */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Availability
            </label>
            <button
              type="button"
              onClick={() => {
                setStagedFilters((prev) =>
                  prev.availabilityDate &&
                  !prev.availabilityFrom &&
                  !prev.availabilityTo
                    ? {
                        ...prev,
                        availabilityDate: null,
                        availabilityFrom: null,
                        availabilityTo: null,
                      }
                    : {
                        ...prev,
                        availabilityDate: getTodayInZone(
                          prev.availabilityZone || "America/New_York",
                        ),
                        availabilityFrom: null,
                        availabilityTo: null,
                      },
                );
              }}
              className={`w-full h-10 rounded border px-3 text-left text-sm
                ${
                  stagedFilters.availabilityDate &&
                  !stagedFilters.availabilityFrom &&
                  !stagedFilters.availabilityTo
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50"
                }`}
            >
              {stagedFilters.availabilityDate &&
              !stagedFilters.availabilityFrom &&
              !stagedFilters.availabilityTo
                ? "Available today ✓"
                : "Available today"}
            </button>
          </div>

          {/* Available Between */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsAvailBetweenOpen((o) => !o)}
              className={`w-full h-10 rounded border px-3 flex items-center justify-between text-sm
                ${
                  stagedFilters.availabilityFrom && stagedFilters.availabilityTo
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50"
                }`}
              aria-expanded={isAvailBetweenOpen}
            >
              <span>
                {stagedFilters.availabilityFrom && stagedFilters.availabilityTo
                  ? `${stagedFilters.availabilityFrom}–${stagedFilters.availabilityTo}`
                  : "Available between…"}
              </span>
              <svg
                className={`h-4 w-4 transition-transform ${isAvailBetweenOpen ? "rotate-180" : ""}`}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" />
              </svg>
            </button>

            {isAvailBetweenOpen && (
              <div className="mt-2 rounded-md border bg-white shadow p-3">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600 w-16">Start</label>
                  <input
                    type="time"
                    value={stagedFilters.availabilityFrom || ""}
                    onChange={(e) =>
                      setStagedFilters((prev) => ({
                        ...prev,
                        availabilityFrom: e.target.value,
                      }))
                    }
                    className="w-full border rounded px-2 py-1"
                  />
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <label className="text-sm text-gray-600 w-16">End</label>
                  <input
                    type="time"
                    value={stagedFilters.availabilityTo || ""}
                    onChange={(e) =>
                      setStagedFilters((prev) => ({
                        ...prev,
                        availabilityTo: e.target.value,
                      }))
                    }
                    className="w-full border rounded px-2 py-1"
                  />
                </div>
                <div className="flex justify-between mt-3">
                  <button
                    type="button"
                    onClick={() => {
                      setStagedFilters((prev) => ({
                        ...prev,
                        availabilityDate: getTodayInZone(
                          prev.availabilityZone || "America/New_York",
                        ),
                        availabilityFrom: prev.availabilityFrom || null,
                        availabilityTo: prev.availabilityTo || null,
                      }));
                      setIsAvailBetweenOpen(false);
                    }}
                    className="px-3 py-1 rounded bg-blue-600 text-white text-sm"
                  >
                    Apply
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStagedFilters((prev) => ({
                        ...prev,
                        availabilityDate: null,
                        availabilityFrom: null,
                        availabilityTo: null,
                      }));
                      setIsAvailBetweenOpen(false);
                    }}
                    className="px-3 py-1 rounded border text-gray-700 text-sm"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Telehealth */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Telehealth
            </label>
            <select
              value={
                stagedFilters.availabilityTelehealth === null ||
                stagedFilters.availabilityTelehealth === undefined
                  ? ""
                  : String(stagedFilters.availabilityTelehealth)
              }
              onChange={(e) => {
                const v = e.target.value;
                setStagedFilters((prev) => ({
                  ...prev,
                  availabilityTelehealth: v === "" ? null : v === "true",
                }));
              }}
              className={activeSelectClass(
                stagedFilters.availabilityTelehealth !== null &&
                  stagedFilters.availabilityTelehealth !== undefined,
              )}
            >
              <option value="">Telehealth: Any</option>
              <option value="true">Telehealth: Yes</option>
              <option value="false">Telehealth: No</option>
            </select>
          </div>

          {/* Experience range */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Years of Experience
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                min="0"
                value={minYearsOfExperience ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setMinYearsOfExperience(v === "" ? null : Number(v));
                }}
                placeholder="Min"
                className={`w-full h-10 rounded border px-3 text-sm ${
                  minYearsOfExperience !== null
                    ? "bg-blue-600 text-white border-blue-600 placeholder-blue-200"
                    : "bg-white text-gray-800 border-gray-300"
                }`}
              />
              <input
                type="number"
                min="0"
                value={maxYearsOfExperience ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setMaxYearsOfExperience(v === "" ? null : Number(v));
                }}
                placeholder="Max"
                className={`w-full h-10 rounded border px-3 text-sm ${
                  maxYearsOfExperience !== null
                    ? "bg-blue-600 text-white border-blue-600 placeholder-blue-200"
                    : "bg-white text-gray-800 border-gray-300"
                }`}
              />
            </div>
          </div>
        </div>

        {/* Sticky bottom bar */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-white">
          <button
            type="button"
            onClick={onReset}
            className="px-5 py-2 rounded-full border border-gray-300 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={onApply}
            className="px-5 py-2 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm transition-colors"
          >
            Apply Results
          </button>
        </div>
      </div>
    </>
  );
}
